import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  message
} from "antd";
import { DeleteOutlined, EditOutlined, PlayCircleOutlined, PlusOutlined } from "@ant-design/icons";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import MoneyInput from "../../../serviceProvider/components/accounting/shared/MoneyInput";
import Amount from "../../../serviceProvider/components/accounting/shared/Amount";
import { formatDate } from "../../../serviceProvider/components/accounting/shared/money";
import * as api from "../../../serviceProvider/components/accounting/recurring/api";
import "../../../serviceProvider/components/accounting/shared/accounting.css";
import CustomerBranchPicker from "../CustomerBranchPicker";
import CustomerSelect from "../CustomerSelect";
import { branchLabel, branchOfDoc, findBranch } from "../customerBranches";

// Wiederkehrende Rechnungen (RI-1) inside the Invoices module: the templates of the tenant, the
// invoices each one created, edit / run now / delete. New templates are written with the
// invoice form ("Repeat this invoice" in AddInvoices.jsx); the daily job creates the invoices.

export const INTERVALS = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "half_yearly", label: "Half-yearly" },
  { value: "yearly", label: "Yearly" }
];

const customerLabel = c =>
  c
    ? [c.company, [c.firstName, c.lastName].filter(Boolean).join(" ")].filter(Boolean).join(" · ")
    : "";
const toDay = v => (v ? dayjs(v) : null);
const fromDay = v => (v ? v.format("YYYY-MM-DD") : "");
const lineNet = l =>
  Math.round(
    Number(l?.unitPrice || 0) * Number(l?.quantity ?? 1) * (1 - Number(l?.discount || 0) / 100)
  );

const TemplateModal = ({ open, template, customers, onClose, onSaved }) => {
  const { t } = useTranslation("accounting");
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [branchSel, setBranchSel] = useState({ clientBranch: null, billTo: "branch" });
  const lines = Form.useWatch("items", form) || [];
  const clientId = Form.useWatch("client", form);
  const selectedCustomer = useMemo(
    () => customers.find(c => String(c._id) === String(clientId)) || null,
    [customers, clientId]
  );

  useEffect(() => {
    if (!open) return;
    const tpl = template || {};
    setBranchSel({ clientBranch: tpl.clientBranch || null, billTo: tpl.billTo || "branch" });
    form.setFieldsValue({
      title: tpl.title || "",
      client: tpl.client?._id || tpl.client || undefined,
      interval: tpl.interval || "monthly",
      startDate: toDay(tpl.startDate) || dayjs().startOf("month"),
      endDate: toDay(tpl.endDate),
      nextDate: toDay(tpl.nextDate),
      autoSend: Boolean(tpl.autoSend),
      directDebit: Boolean(tpl.directDebit),
      dueDays: tpl.paymentTerms?.dueDays ?? 14,
      description: tpl.description || "",
      items: (tpl.items?.length
        ? tpl.items
        : [{ quantity: 1, unit: "Pauschale", taxRate: 19 }]
      ).map(i => ({
        description: i.description || "",
        quantity: i.quantity ?? 1,
        unit: i.unit || "",
        unitPrice: i.unitPrice ?? null,
        taxRate: i.taxRate ?? 19,
        costType: i.costType || ""
      })),
      adjustmentDate: toDay(tpl.priceAdjustment?.date),
      adjustmentPercent: tpl.priceAdjustment?.percent || null
    });
  }, [open, template, form]);

  const submit = async () => {
    let v;
    try {
      v = await form.validateFields();
    } catch {
      return;
    }
    const body = {
      title: v.title,
      client: v.client,
      clientBranch: branchSel.clientBranch || null,
      billTo: branchSel.billTo || "branch",
      interval: v.interval,
      startDate: fromDay(v.startDate),
      endDate: fromDay(v.endDate),
      autoSend: Boolean(v.autoSend),
      directDebit: Boolean(v.directDebit),
      paymentTerms: { ...(template?.paymentTerms || {}), dueDays: v.dueDays ?? 14 },
      description: v.description || "",
      items: (v.items || []).map(i => ({
        description: i.description,
        quantity: i.quantity ?? 1,
        unit: i.unit || "",
        unitPrice: i.unitPrice || 0,
        taxRate: i.taxRate ?? 19,
        ...(i.costType ? { costType: i.costType } : {})
      })),
      priceAdjustment: {
        date: fromDay(v.adjustmentDate),
        percent: v.adjustmentPercent || 0
      }
    };
    if (v.nextDate) body.nextDate = fromDay(v.nextDate);
    setSaving(true);
    try {
      if (template?._id) await api.updateRecurring(template._id, body);
      else await api.createRecurring(body);
      message.success(t("Recurring invoice saved"));
      onSaved();
      onClose();
    } catch (err) {
      message.error(api.errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const total = lines.reduce((s, l) => s + lineNet(l), 0);
  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={submit}
      confirmLoading={saving}
      okText={t("Save")}
      width={880}
      title={template?._id ? t("Edit recurring invoice") : t("New recurring invoice")}
      rootClassName="acct-module"
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Row gutter={[12, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name="title" label={t("Title")} rules={[{ required: true }]}>
              <Input placeholder={t("e.g. Maintenance contract heating")} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="client" label={t("Customer")} rules={[{ required: true }]}>
              <CustomerSelect
                customers={customers}
                branch={branchSel.clientBranch}
                onChange={(id, branchId, customer) =>
                  setBranchSel({
                    clientBranch: branchId,
                    billTo: (branchId && findBranch(customer, branchId)?.defaultBillTo) || "branch"
                  })
                }
              />
            </Form.Item>
          </Col>
          {selectedCustomer?.branches?.length ? (
            <Col xs={24}>
              <CustomerBranchPicker
                customer={selectedCustomer}
                value={branchSel}
                onChange={setBranchSel}
                showBranchSelect={false}
              />
            </Col>
          ) : null}
          <Col xs={12} md={6}>
            <Form.Item name="interval" label={t("Interval")} rules={[{ required: true }]}>
              <Select options={INTERVALS.map(i => ({ value: i.value, label: t(i.label) }))} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="startDate" label={t("Start date")} rules={[{ required: true }]}>
              <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="endDate" label={t("End date")}>
              <DatePicker
                format="DD.MM.YYYY"
                style={{ width: "100%" }}
                placeholder={t("Open-ended")}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              name="nextDate"
              label={t("Next period starts")}
              tooltip={t("Start of the next Leistungszeitraum to invoice")}
            >
              <DatePicker
                format="DD.MM.YYYY"
                style={{ width: "100%" }}
                placeholder={t("Start date")}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="dueDays" label={t("Due in days")}>
              <InputNumber min={0} max={365} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              name="autoSend"
              label={t("Send automatically")}
              valuePropName="checked"
              tooltip={t("Off: a draft is created for review")}
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="directDebit" label={t("Direct debit")} valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
        <Typography.Title level={5}>{t("Lines")}</Typography.Title>
        <Form.List name="items">
          {(fields, { add, remove }) => (
            <>
              {fields.map(field => (
                <Row gutter={[8, 0]} key={field.key} align="bottom">
                  <Col xs={24} md={9}>
                    <Form.Item
                      name={[field.name, "description"]}
                      label={t("Description")}
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={8} md={3}>
                    <Form.Item name={[field.name, "quantity"]} label={t("Quantity")}>
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={8} md={3}>
                    <Form.Item name={[field.name, "unit"]} label={t("Unit")}>
                      <Input />
                    </Form.Item>
                    {/* § 35a cost type from the invoice form, kept across edits */}
                    <Form.Item name={[field.name, "costType"]} hidden>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={8} md={4}>
                    <Form.Item name={[field.name, "unitPrice"]} label={t("Unit price")}>
                      <MoneyInput />
                    </Form.Item>
                  </Col>
                  <Col xs={16} md={3}>
                    <Form.Item name={[field.name, "taxRate"]} label={t("Tax rate")}>
                      <Select options={[19, 7, 0].map(r => ({ value: r, label: `${r} %` }))} />
                    </Form.Item>
                  </Col>
                  <Col xs={8} md={2}>
                    <Form.Item label=" ">
                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        aria-label={t("Remove")}
                        disabled={fields.length <= 1}
                        onClick={() => remove(field.name)}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ))}
              <Space style={{ justifyContent: "space-between", width: "100%" }} wrap>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => add({ quantity: 1, unit: "Pauschale", taxRate: 19 })}
                >
                  {t("Add line")}
                </Button>
                <Typography.Text strong>
                  {t("Net per period")}: <Amount cents={total} strong />
                </Typography.Text>
              </Space>
            </>
          )}
        </Form.List>
        <Row gutter={[12, 0]} style={{ marginTop: 16 }}>
          <Col xs={12} md={6}>
            <Form.Item name="adjustmentDate" label={t("Price adjustment from")}>
              <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="adjustmentPercent" label={t("Adjustment (%)")}>
              <InputNumber
                min={-50}
                max={100}
                step={0.5}
                style={{ width: "100%" }}
                addonAfter="%"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="description" label={t("Invoice text")}>
              <Input.TextArea rows={2} />
            </Form.Item>
          </Col>
        </Row>
        {template?.priceAdjustment?.appliedAt ? (
          <Alert
            type="info"
            showIcon
            message={`${t("Price adjustment applied on")} ${formatDate(
              String(template.priceAdjustment.appliedAt).slice(0, 10)
            )}`}
          />
        ) : null}
      </Form>
    </Modal>
  );
};

/**
 * List of the recurring invoice templates. `onNew` opens the invoice form with "Repeat this
 * invoice" switched on; `reloadKey` changes after such a save so the list reloads.
 */
const RecurringInvoices = ({ onNew, reloadKey }) => {
  const { t } = useTranslation("accounting");
  const isMobile = useIsMobile();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | template
  const [busy, setBusy] = useState("");
  const [customers, setCustomers] = useState([]);
  useEffect(() => {
    api
      .listCustomers()
      .then(setCustomers)
      .catch(() => setCustomers([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listRecurring();
      setTemplates(Array.isArray(res.data?.templates) ? res.data.templates : []);
    } catch (err) {
      message.error(api.errorText(err));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const summarize = runs => {
    const list = Array.isArray(runs) ? runs : [];
    const created = list.filter(r => r.invoice && !r.skipped).length;
    const errors = list.filter(r => r.error);
    if (errors.length) message.warning(`${t("Errors")}: ${errors.map(e => e.error).join("; ")}`);
    message.success(`${t("Invoices created")}: ${created}`);
  };

  const runOne = async tpl => {
    setBusy(tpl._id);
    try {
      const res = await api.runRecurring(tpl._id);
      summarize(res.data?.runs);
      load();
    } catch (err) {
      message.error(api.errorText(err));
    } finally {
      setBusy("");
    }
  };
  const runAll = async () => {
    setBusy("all");
    try {
      const res = await api.runDueRecurring();
      message.success(
        `${t("Invoices created")}: ${res.data?.created || 0} · ${t("Sent")}: ${res.data?.sent || 0}`
      );
      if (res.data?.errors) message.warning(`${t("Errors")}: ${res.data.errors}`);
      load();
    } catch (err) {
      message.error(api.errorText(err));
    } finally {
      setBusy("");
    }
  };
  const remove = async tpl => {
    try {
      const res = await api.deleteRecurring(tpl._id);
      message.success(res.data?.deleted ? t("Deleted") : t("Deactivated (invoices exist)"));
      load();
    } catch (err) {
      message.error(api.errorText(err));
    }
  };

  const columns = [
    {
      title: t("Title"),
      dataIndex: "title",
      mobile: "title",
      render: (v, r) => (
        <Space direction="vertical" size={0} className="acct-wrap">
          <Typography.Text strong>{v}</Typography.Text>
          <Typography.Text type="secondary">
            {customerLabel(r.client)}
            {branchOfDoc(r) ? ` · ${branchLabel(branchOfDoc(r))}` : ""}
          </Typography.Text>
        </Space>
      )
    },
    {
      title: t("Interval"),
      dataIndex: "interval",
      render: v => t(INTERVALS.find(i => i.value === v)?.label || v)
    },
    {
      title: t("Net per period"),
      key: "net",
      align: "end",
      render: (_, r) => <Amount cents={(r.items || []).reduce((s, l) => s + lineNet(l), 0)} />
    },
    {
      title: t("Next period starts"),
      dataIndex: "nextDate",
      mobile: "subtitle",
      render: (v, r) => (
        <span className="acct-num">
          {r.active ? formatDate(v) : "—"}
          {r.endDate ? ` (${t("until")} ${formatDate(r.endDate)})` : ""}
        </span>
      )
    },
    {
      title: t("Status"),
      key: "flags",
      render: (_, r) => {
        const lastError = [...(r.runs || [])].reverse().find(x => x.error);
        return (
          <Space size={4} wrap>
            <Tag color={r.active ? "green" : "default"}>
              {r.active ? t("Active") : t("Inactive")}
            </Tag>
            <Tag>{r.autoSend ? t("Send automatically") : t("Draft")}</Tag>
            {r.directDebit ? <Tag color="blue">{t("Direct debit")}</Tag> : null}
            {r.priceAdjustment?.percent && !r.priceAdjustment?.appliedAt ? (
              <Tag color="gold">
                {r.priceAdjustment.percent > 0 ? "+" : ""}
                {r.priceAdjustment.percent} % {t("from")} {formatDate(r.priceAdjustment.date)}
              </Tag>
            ) : null}
            {lastError ? (
              <Tag color="red" title={lastError.error}>
                {t("Error")}
              </Tag>
            ) : null}
          </Space>
        );
      }
    },
    {
      title: t("Invoices"),
      key: "runs",
      render: (_, r) => (r.runs || []).filter(x => x.invoice).length
    },
    {
      title: t("Actions"),
      key: "actions",
      mobile: "actions",
      render: (_, r) => (
        <Space wrap className="acct-actions">
          <Button size="small" icon={<EditOutlined />} onClick={() => setEditing(r)}>
            {t("Edit")}
          </Button>
          <Button
            size="small"
            icon={<PlayCircleOutlined />}
            loading={busy === r._id}
            disabled={!r.active}
            onClick={() => runOne(r)}
          >
            {t("Run now")}
          </Button>
          <Popconfirm title={t("Delete this recurring invoice?")} onConfirm={() => remove(r)}>
            <Button size="small" danger icon={<DeleteOutlined />} aria-label={t("Delete")} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const runRows = r =>
    (r.runs || [])
      .slice()
      .reverse()
      .map((x, i) => ({ ...x, key: `${x.period}-${i}` }));

  return (
    <div className="acct-module">
      <Space style={{ marginBottom: 12 }} wrap>
        {onNew ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={onNew}>
            {t("New recurring invoice")}
          </Button>
        ) : null}
        <Button icon={<PlayCircleOutlined />} loading={busy === "all"} onClick={runAll}>
          {t("Create due invoices now")}
        </Button>
      </Space>
      <ResponsiveTable
        rowKey="_id"
        size="small"
        loading={loading}
        dataSource={templates}
        columns={columns}
        pagination={false}
        expandable={
          isMobile
            ? undefined
            : {
                rowExpandable: r => (r.runs || []).length > 0,
                expandedRowRender: r => (
                  <ResponsiveTable
                    rowKey="key"
                    size="small"
                    pagination={false}
                    dataSource={runRows(r)}
                    columns={[
                      {
                        title: t("Period"),
                        dataIndex: "period",
                        render: v =>
                          String(v || "")
                            .split("..")
                            .map(formatDate)
                            .join(" – ")
                      },
                      {
                        title: t("Invoice"),
                        dataIndex: "invoice",
                        render: v =>
                          v ? (
                            <span>
                              <Link to={`/invoices/details/${v._id || v}`}>
                                {v.invoiceNumber || t("Draft")}
                              </Link>{" "}
                              {v.amounts?.gross !== undefined ? (
                                <Amount cents={v.amounts.gross} />
                              ) : null}
                            </span>
                          ) : (
                            "—"
                          )
                      },
                      {
                        title: t("Error"),
                        dataIndex: "error",
                        render: v => (v ? <Typography.Text type="danger">{v}</Typography.Text> : "")
                      }
                    ]}
                  />
                )
              }
        }
      />
      <TemplateModal
        open={Boolean(editing)}
        template={editing && editing._id ? editing : null}
        customers={customers}
        onClose={() => setEditing(null)}
        onSaved={load}
      />
    </div>
  );
};

export default RecurringInvoices;
